import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, TrendingUp, TrendingDown, Minus, MessageSquare, X, Edit, Trash2, ArrowLeft } from "lucide-react";
import PageShell from "@/components/brand/PageShell";
import { api } from "@/lib/api";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from "@/components/ui/dialog";

function outcomeBadge(outcome) {
    switch (outcome) {
        case "win": return { label: "WIN", bg: "#ECFDF3", fg: "#067647" };
        case "loss": return { label: "LOSS", bg: "#FEF3F2", fg: "#B42318" };
        case "breakeven": return { label: "B/E", bg: "var(--st-silver-100)", fg: "var(--st-ink-muted)" };
        case "open": return { label: "OPEN", bg: "#EEF2FE", fg: "var(--st-royal)" };
        default: return { label: outcome, bg: "var(--st-silver-100)", fg: "var(--st-ink-muted)" };
    }
}

function fmtPct(v) {
    // _pct fields from the API are already percentages (10.0 = 10%).
    if (v === null || v === undefined) return "—";
    return `${Number(v).toFixed(2)}%`;
}
function fmtCurrency(v) {
    if (v === null || v === undefined) return "—";
    const sign = v < 0 ? "-" : "";
    return `${sign}$${Math.abs(v).toFixed(2)}`;
}
function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
    catch { return iso; }
}

function StatBar({ stats, testId = "journal-stats-bar" }) {
    if (!stats) return null;
    return (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid={testId}>
            {[
                { label: "Total entries", value: stats.total_entries, testId: "stat-total" },
                { label: "Win rate", value: stats.closed_count ? `${Math.round(stats.win_rate * 100)}%` : "—", testId: "stat-win-rate" },
                { label: "Avg win", value: stats.win_count ? fmtPct(stats.avg_win_pct) : "—", testId: "stat-avg-win" },
                { label: "Avg loss", value: stats.loss_count ? fmtPct(stats.avg_loss_pct) : "—", testId: "stat-avg-loss" },
                { label: "Total P&L", value: fmtCurrency(stats.total_pnl_absolute), testId: "stat-total-pnl" },
            ].map((s) => (
                <div key={s.label} className="st-surface p-4" data-testid={s.testId}>
                    <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>
                        {s.label}
                    </div>
                    <div className="font-serif text-xl mt-1" style={{ color: "var(--st-navy)" }}>{s.value}</div>
                </div>
            ))}
        </div>
    );
}

function EntryFormModal({ open, onClose, onSaved, editing = null }) {
    const [form, setForm] = useState(() => editing ? {
        mode: editing.mode, ticker: editing.ticker, direction: editing.direction,
        entry_price: editing.entry_price, position_size: editing.position_size,
        entry_date: (editing.entry_date || "").slice(0, 16),
        thesis: editing.thesis || "", notes: editing.notes || "",
        exit_price: editing.exit_price || "", exit_date: (editing.exit_date || "").slice(0, 16),
        close_now: !!editing.exit_price,
    } : {
        mode: "paper", ticker: "", direction: "long",
        entry_price: "", position_size: "",
        entry_date: new Date().toISOString().slice(0, 16),
        thesis: "", notes: "",
        exit_price: "", exit_date: "", close_now: false,
    });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    async function handleSubmit(e) {
        e.preventDefault();
        setError(null);
        if ((form.thesis || "").trim().length < 20) {
            setError("Thesis must be at least 20 characters.");
            return;
        }
        setSubmitting(true);
        try {
            const payload = {
                mode: form.mode,
                ticker: form.ticker.toUpperCase().trim(),
                direction: form.direction,
                entry_price: parseFloat(form.entry_price),
                position_size: parseFloat(form.position_size),
                entry_date: new Date(form.entry_date).toISOString(),
                thesis: form.thesis.trim(),
                notes: form.notes?.trim() || null,
            };
            if (form.close_now && form.exit_price && form.exit_date) {
                payload.exit_price = parseFloat(form.exit_price);
                payload.exit_date = new Date(form.exit_date).toISOString();
            }
            const saved = editing
                ? await api.updateJournalEntry(editing.id, payload)
                : await api.createJournalEntry(payload);
            onSaved(saved);
            onClose();
        } catch (err) {
            setError(err.message || "Failed to save.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-lg" data-testid="journal-entry-modal">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl" style={{ color: "var(--st-navy)" }}>
                        {editing ? "Edit trade entry" : "New trade entry"}
                    </DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4" data-testid="journal-entry-form">
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="st-label">Mode</label>
                            <div className="flex gap-3">
                                {["paper", "live"].map((m) => (
                                    <label key={m} className="flex items-center gap-2 text-sm">
                                        <input type="radio" name="mode" value={m} checked={form.mode === m}
                                               onChange={() => setForm({ ...form, mode: m })}
                                               data-testid={`entry-mode-${m}`} />
                                        {m.charAt(0).toUpperCase() + m.slice(1)}
                                    </label>
                                ))}
                            </div>
                        </div>
                        <div>
                            <label className="st-label">Direction</label>
                            <div className="flex gap-3">
                                {["long", "short"].map((d) => (
                                    <label key={d} className="flex items-center gap-2 text-sm">
                                        <input type="radio" name="direction" value={d} checked={form.direction === d}
                                               onChange={() => setForm({ ...form, direction: d })}
                                               data-testid={`entry-direction-${d}`} />
                                        {d.charAt(0).toUpperCase() + d.slice(1)}
                                    </label>
                                ))}
                            </div>
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="st-label">Ticker</label>
                            <input required className="st-input uppercase" value={form.ticker}
                                   onChange={(e) => setForm({ ...form, ticker: e.target.value.toUpperCase() })}
                                   data-testid="entry-ticker" />
                        </div>
                        <div>
                            <label className="st-label">Position size</label>
                            <input required type="number" min="0.0001" step="any" className="st-input" value={form.position_size}
                                   onChange={(e) => setForm({ ...form, position_size: e.target.value })}
                                   data-testid="entry-position-size" />
                        </div>
                        <div>
                            <label className="st-label">Entry price</label>
                            <input required type="number" min="0.0001" step="any" className="st-input" value={form.entry_price}
                                   onChange={(e) => setForm({ ...form, entry_price: e.target.value })}
                                   data-testid="entry-entry-price" />
                        </div>
                        <div>
                            <label className="st-label">Entry date/time</label>
                            <input required type="datetime-local" className="st-input" value={form.entry_date}
                                   onChange={(e) => setForm({ ...form, entry_date: e.target.value })}
                                   data-testid="entry-entry-date" />
                        </div>
                    </div>

                    <div>
                        <label className="st-label">Thesis <span style={{ color: "var(--st-silver-500)" }}>(min 20 chars)</span></label>
                        <textarea required rows={3} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }} value={form.thesis}
                                  onChange={(e) => setForm({ ...form, thesis: e.target.value })}
                                  data-testid="entry-thesis" />
                    </div>

                    <div>
                        <label className="st-label">Notes (optional)</label>
                        <textarea rows={2} className="st-input" style={{ height: "auto", padding: "0.5rem 0.9rem" }} value={form.notes}
                                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                  data-testid="entry-notes" />
                    </div>

                    <label className="flex items-center gap-2 text-sm">
                        <input type="checkbox" checked={form.close_now}
                               onChange={(e) => setForm({ ...form, close_now: e.target.checked })}
                               data-testid="entry-close-now" />
                        <span>Close trade now (record exit)</span>
                    </label>

                    {form.close_now && (
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label className="st-label">Exit price</label>
                                <input required={form.close_now} type="number" min="0.0001" step="any" className="st-input"
                                       value={form.exit_price}
                                       onChange={(e) => setForm({ ...form, exit_price: e.target.value })}
                                       data-testid="entry-exit-price" />
                            </div>
                            <div>
                                <label className="st-label">Exit date/time</label>
                                <input required={form.close_now} type="datetime-local" className="st-input"
                                       value={form.exit_date}
                                       onChange={(e) => setForm({ ...form, exit_date: e.target.value })}
                                       data-testid="entry-exit-date" />
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="text-sm px-3 py-2 rounded"
                             style={{ background: "#FEF1F2", color: "#B42318", border: "1px solid #FECDCA" }}
                             data-testid="entry-form-error">
                            {error}
                        </div>
                    )}

                    <DialogFooter>
                        <button type="button" className="btn-ghost text-sm" onClick={onClose} data-testid="entry-cancel-btn">
                            Cancel
                        </button>
                        <button type="submit" disabled={submitting} className="btn-royal text-sm" data-testid="entry-submit-btn">
                            {submitting ? "Saving…" : editing ? "Save changes" : "Save entry"}
                        </button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}

function EntryDrawer({ entry, onClose, onChanged }) {
    const [comments, setComments] = useState([]);
    const [closing, setClosing] = useState(false);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        if (!entry) return;
        api.listJournalComments(entry.id).then((r) => setComments(r.comments || []));
    }, [entry]);

    if (!entry) return null;
    const badge = outcomeBadge(entry.outcome);

    async function handleDelete() {
        if (!window.confirm("Delete this entry?")) return;
        await api.deleteJournalEntry(entry.id);
        onChanged();
        onClose();
    }

    async function handleClose() {
        const price = prompt("Exit price?");
        if (!price) return;
        const patch = { exit_price: parseFloat(price), exit_date: new Date().toISOString() };
        await api.updateJournalEntry(entry.id, patch);
        onChanged();
        onClose();
    }

    return (
        <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="max-w-xl" data-testid="entry-drawer">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl flex items-center gap-3" style={{ color: "var(--st-navy)" }}>
                        {entry.ticker} · {entry.direction.toUpperCase()}
                        <span className="text-[10px] tracking-caps font-bold px-2 py-0.5 rounded"
                              style={{ background: badge.bg, color: badge.fg }}>
                            {badge.label}
                        </span>
                    </DialogTitle>
                </DialogHeader>

                <div className="space-y-4 text-sm">
                    <div className="grid grid-cols-2 gap-4">
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Mode</span>
                            <div style={{ color: "var(--st-ink)" }}>{entry.mode}</div></div>
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Size</span>
                            <div style={{ color: "var(--st-ink)" }}>{entry.position_size}</div></div>
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Entry</span>
                            <div style={{ color: "var(--st-ink)" }}>${entry.entry_price} · {fmtDate(entry.entry_date)}</div></div>
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Exit</span>
                            <div style={{ color: "var(--st-ink)" }}>
                                {entry.exit_price ? `$${entry.exit_price} · ${fmtDate(entry.exit_date)}` : "Open"}
                            </div></div>
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>P&L %</span>
                            <div style={{ color: "var(--st-ink)" }} data-testid="drawer-pnl-pct">{fmtPct(entry.pnl_percent)}</div></div>
                        <div><span className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>P&L $</span>
                            <div style={{ color: "var(--st-ink)" }}>{fmtCurrency(entry.pnl_absolute)}</div></div>
                    </div>

                    <div>
                        <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Thesis</div>
                        <p className="mt-1 leading-relaxed" style={{ color: "var(--st-ink)" }}>{entry.thesis}</p>
                    </div>

                    {entry.notes && (
                        <div>
                            <div className="tracking-caps text-[10px] font-semibold" style={{ color: "var(--st-silver-500)" }}>Notes</div>
                            <p className="mt-1 leading-relaxed" style={{ color: "var(--st-ink)" }}>{entry.notes}</p>
                        </div>
                    )}

                    <div className="pt-4" style={{ borderTop: "1px solid var(--st-line)" }}>
                        <div className="tracking-caps text-[10px] font-semibold flex items-center gap-2" style={{ color: "var(--st-silver-500)" }}>
                            <MessageSquare size={12} /> Instructor comments
                        </div>
                        {comments.length === 0 ? (
                            <p className="mt-2 text-xs italic" style={{ color: "var(--st-silver-500)" }}>No comments yet.</p>
                        ) : (
                            <ul className="mt-2 space-y-3" data-testid="entry-comments-list">
                                {comments.map((c) => (
                                    <li key={c.id} className="p-3 rounded" style={{ background: "var(--st-silver-50)" }}>
                                        <div className="text-xs font-semibold" style={{ color: "var(--st-navy)" }}>{c.author_name}</div>
                                        <p className="mt-1 text-sm" style={{ color: "var(--st-ink)" }}>{c.body}</p>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </div>

                <DialogFooter className="flex-wrap gap-2">
                    {!entry.exit_price && (
                        <button type="button" className="btn-royal text-sm" onClick={handleClose} data-testid="drawer-close-trade-btn">
                            Close trade
                        </button>
                    )}
                    <button type="button" className="btn-outline-navy text-sm inline-flex items-center gap-1"
                            onClick={() => setEditing(true)} data-testid="drawer-edit-btn">
                        <Edit size={14} /> Edit
                    </button>
                    <button type="button" className="btn-ghost text-sm inline-flex items-center gap-1"
                            onClick={handleDelete} data-testid="drawer-delete-btn"
                            style={{ color: "#B42318" }}>
                        <Trash2 size={14} /> Delete
                    </button>
                </DialogFooter>
                {editing && (
                    <EntryFormModal open={editing} onClose={() => setEditing(false)}
                                    onSaved={() => { onChanged(); onClose(); }} editing={entry} />
                )}
            </DialogContent>
        </Dialog>
    );
}

export default function JournalPage() {
    const [tab, setTab] = useState("all");
    const [entries, setEntries] = useState([]);
    const [stats, setStats] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [selected, setSelected] = useState(null);
    const [loading, setLoading] = useState(true);

    async function reload() {
        setLoading(true);
        const params = {};
        if (tab !== "all") params.mode = tab;
        const [list, st] = await Promise.all([api.listJournalEntries(params), api.journalStats()]);
        setEntries(list.entries);
        setStats(st);
        setLoading(false);
    }

    useEffect(() => { reload(); /* eslint-disable-next-line */ }, [tab]);

    return (
        <PageShell>
            <section className="max-w-6xl mx-auto px-6 py-12 space-y-8" data-testid="journal-page">
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-2 text-xs mb-2" style={{ color: "var(--st-silver-500)" }}>
                            <Link to="/dashboard" className="hover:underline flex items-center gap-1">
                                <ArrowLeft size={12} /> Dashboard
                            </Link>
                        </div>
                        <h1 className="font-serif text-4xl" style={{ color: "var(--st-navy)" }} data-testid="journal-heading">
                            Trade Journal
                        </h1>
                        <p className="mt-2 text-sm" style={{ color: "var(--st-ink-muted)" }}>
                            Every trade you log becomes evidence — for you or against you. Grade the decision, not the outcome.
                        </p>
                    </div>
                    <button type="button" className="btn-royal text-sm inline-flex items-center gap-2"
                            onClick={() => setShowForm(true)} data-testid="journal-new-entry-btn">
                        <Plus size={16} strokeWidth={2} /> New entry
                    </button>
                </div>

                <StatBar stats={stats} />

                <div className="flex gap-1" data-testid="journal-tabs">
                    {[
                        { k: "all", label: "All" },
                        { k: "paper", label: "Paper" },
                        { k: "live", label: "Live" },
                    ].map(({ k, label }) => (
                        <button key={k} type="button" onClick={() => setTab(k)}
                                className="px-4 py-2 rounded-t text-sm font-semibold"
                                style={{
                                    background: tab === k ? "var(--st-white)" : "transparent",
                                    color: tab === k ? "var(--st-navy)" : "var(--st-ink-muted)",
                                    borderTop: tab === k ? "2px solid var(--st-royal)" : "2px solid transparent",
                                }}
                                data-testid={`journal-tab-${k}`}>
                            {label}
                        </button>
                    ))}
                </div>

                <div className="st-surface overflow-hidden" data-testid="journal-entries-table">
                    <table className="w-full text-sm">
                        <thead>
                            <tr style={{ background: "var(--st-silver-50)", color: "var(--st-ink-muted)" }}>
                                <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Date</th>
                                <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Ticker</th>
                                <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Dir</th>
                                <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Mode</th>
                                <th className="text-right px-4 py-3 font-semibold tracking-caps text-[10px]">Entry</th>
                                <th className="text-right px-4 py-3 font-semibold tracking-caps text-[10px]">Exit</th>
                                <th className="text-right px-4 py-3 font-semibold tracking-caps text-[10px]">P&L %</th>
                                <th className="text-left px-4 py-3 font-semibold tracking-caps text-[10px]">Outcome</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading && (
                                <tr><td colSpan={8} className="text-center py-8" style={{ color: "var(--st-silver-500)" }}>Loading…</td></tr>
                            )}
                            {!loading && entries.length === 0 && (
                                <tr><td colSpan={8} className="text-center py-8 italic" style={{ color: "var(--st-silver-500)" }} data-testid="journal-empty">
                                    No entries yet. Click "New entry" to log your first trade.
                                </td></tr>
                            )}
                            {entries.map((e) => {
                                const b = outcomeBadge(e.outcome);
                                return (
                                    <tr key={e.id}
                                        onClick={() => setSelected(e)}
                                        className="cursor-pointer hover:bg-silver-50"
                                        style={{ borderTop: "1px solid var(--st-line)", transition: "background-color 0.15s ease" }}
                                        data-testid={`journal-entry-row-${e.id}`}>
                                        <td className="px-4 py-3">{fmtDate(e.entry_date)}</td>
                                        <td className="px-4 py-3 font-semibold" style={{ color: "var(--st-navy)" }}>{e.ticker}</td>
                                        <td className="px-4 py-3">
                                            {e.direction === "long" ? (
                                                <TrendingUp size={14} color="#067647" />
                                            ) : (
                                                <TrendingDown size={14} color="#B42318" />
                                            )}
                                        </td>
                                        <td className="px-4 py-3 text-xs tracking-caps" style={{ color: "var(--st-silver-500)" }}>{e.mode}</td>
                                        <td className="px-4 py-3 text-right">${e.entry_price}</td>
                                        <td className="px-4 py-3 text-right">{e.exit_price ? `$${e.exit_price}` : <Minus size={14} color="var(--st-silver-500)" className="inline" />}</td>
                                        <td className="px-4 py-3 text-right" style={{ color: e.pnl_percent > 0 ? "#067647" : e.pnl_percent < 0 ? "#B42318" : "var(--st-ink)" }}>
                                            {fmtPct(e.pnl_percent)}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span className="text-[10px] tracking-caps font-bold px-2 py-0.5 rounded"
                                                  style={{ background: b.bg, color: b.fg }}>
                                                {b.label}
                                            </span>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </section>

            {showForm && (
                <EntryFormModal open={showForm} onClose={() => setShowForm(false)} onSaved={() => reload()} />
            )}
            {selected && (
                <EntryDrawer entry={selected} onClose={() => setSelected(null)} onChanged={reload} />
            )}
        </PageShell>
    );
}
